import React from 'react';
import { Alert } from 'antd';
import Dropzone from 'components/common/Dropzone';
import ImageGallery from 'components/common/ImageGallery';
import placeholdeImage from 'static/img/car-photo.png';
import { getImageUrl } from 'lib/Utils';
import { PartTypes } from 'vdlib';
import LookupsHelper from 'lib/LookupsHelper';
import SimpleCollapse from 'components/common/SimpleCollapse';

const imagesToUrls = (imagesArr, folderNumber) =>
  imagesArr.map(p => ({
    id: p.Id,
    original: getImageUrl(p.Filename, folderNumber),
    thumbnail: getImageUrl(p.ThumbnailFilename, folderNumber),
  }));

const maxImagesNumber = 5;

const QuickBidPartPhotos = ({
  auctionId,
  deleteAuctionPartImages,
  deleteAuctionPartImagesPending,
  images,
  imagesError,
  imagesPending,
  merchantId,
  onClear,
  onDelete,
  onFetch,
  onUpload,
  partTypes,
  uploaded,
  uploadError,
  uploading,
}) => {
  const filesRef = React.useRef({ files: null });
  const [currentUploadIdx, setCurrentUploadIdx] = React.useState(null);
  const [maxImagesReached, setMaxImagesReached] = React.useState(false);

  // fetch on mount, clear on unmount
  React.useEffect(() => {
    onFetch();

    return () => onClear();
  }, [auctionId]); // eslint-disable-line

  // upload next when upload index changes, or refetch when index > files.length
  React.useEffect(() => {
    if (
      !filesRef.current.files?.length ||
      currentUploadIdx === null ||
      currentUploadIdx < 0
    )
      return;

    if (currentUploadIdx < filesRef.current.files.length) {
      onUpload(filesRef.current.files[currentUploadIdx]);
    } else {
      onFetch();
      onClear();
      setTimeout(() => setCurrentUploadIdx(null), 1000);
    }
  }, [currentUploadIdx]); // eslint-disable-line

  // When uploaded, increment upload index
  React.useEffect(() => {
    if (uploaded) setCurrentUploadIdx(curr => curr + 1);
  }, [uploaded]);

  // Reset maxImagesReached state after 1 sec
  React.useEffect(() => {
    if (maxImagesReached) setTimeout(() => setMaxImagesReached(false), 1000);
  }, [maxImagesReached]);

  // Refecth images when deleted
  React.useEffect(() => {
    if (deleteAuctionPartImages) onFetch();
  }, [deleteAuctionPartImages]); //eslint-disable-line

  // transform image filenames to full image/thumb urls
  const imageUrls = React.useMemo(() => {
    if (!Array.isArray(images)) return [];
    const directoryKey = `${auctionId}_${merchantId}_${LookupsHelper.getIdByDescription(
      partTypes,
      PartTypes.Used
    )}`;
    return imagesToUrls(
      images.filter(img => img.AuctionId === auctionId),
      directoryKey
    );
  }, [partTypes, images, auctionId, merchantId]);

  // delete image when red X pressed
  const handleThumbnailClick = React.useCallback(
    (event, idx) => {
      if (event.target.tagName.toLowerCase() === 'div') {
        if (event.stopImmediatePropagation) event.stopImmediatePropagation();
        if (event.stopPropagation) event.stopPropagation();
        event.preventDefault();
        onDelete(imageUrls[idx].id);
      }
    },
    [imageUrls] // eslint-disable-line
  );

  // begin upload process when file dropped in dropzone
  const handleDrop = React.useCallback(
    filesToUpload => {
      if (filesToUpload.length + images.length > 5)
        return setMaxImagesReached(true);

      filesRef.current.files = filesToUpload;
      setCurrentUploadIdx(0);
      setMaxImagesReached(false);
    },
    [images]
  );

  const success = currentUploadIdx >= filesRef.current.files?.length;

  return (
    <div className="page-content">
      <Alert
        type="info"
        message={
          <>
            Μπορείτε να ανεβάσετε έως 5 φωτογραφίες του{' '}
            <span className="label label-danger">μεταχειρισμένου</span>{' '}
            ανταλλακτικού
          </>
        }
      />
      <div style={{ margin: '30px 0' }}>
        {Array.isArray(imageUrls) && imageUrls.length > 0 ? (
          <div className="deletable-gallery">
            <ImageGallery
              items={imageUrls}
              onThumbnailClick={handleThumbnailClick}
              renderFullscreenButton={(onClick, isFullscreen) => (
                <button
                  type="button"
                  className={`image-gallery-fullscreen-button ${
                    isFullscreen ? ' active' : 'inactive'
                  }`}
                  onClick={onClick}
                >
                  {!isFullscreen && <i className="fa fa-search-plus" />}
                </button>
              )}
            />
          </div>
        ) : (
          <div className="image-placeholder" style={{ textAlign: 'center' }}>
            <img src={placeholdeImage} alt="" style={{ width: 200 }} />
          </div>
        )}
      </div>
      <SimpleCollapse in={!imageUrls || imageUrls.length < maxImagesNumber}>
        <Dropzone
          accept="images"
          current={currentUploadIdx + 1}
          error={uploadError}
          loading={uploading || deleteAuctionPartImagesPending}
          maxFilesNumber={maxImagesNumber}
          maxFilesReached={maxImagesReached}
          onDrop={handleDrop}
          success={success}
          total={filesRef.current.files?.length || 0}
        />
      </SimpleCollapse>
    </div>
  );
};

export default QuickBidPartPhotos;
